import { toNumber } from '@/utils/format';
import type {
  PosMoneyResult,
  PosServiceLineKind,
  PosServiceLineResolved,
  PosTaxGroup,
  PosVatSource,
  ShippingFeePayer,
  ShippingMode,
} from '@tcr-erp/shared';

/**
 * `orderToMoneyResult` — dịch một đơn ĐÃ LƯU thành `PosMoneyResult` để màn chi
 * tiết và màn sửa đơn hiển thị (cùng `OrderInfoPanel`/`ShippingTotals` mà POS
 * dùng lúc tạo đơn).
 *
 * NGUYÊN TẮC SỐNG CÒN: đọc số đã lưu trong `summary`/`service_lines`, KHÔNG gọi
 * lại `computePosMoney`. Giá dịch vụ, thuế suất, VAT mặc định đều đổi được về
 * sau — tính lại sẽ làm đơn cũ hiện số khác số đã thu của khách và khác cả hoá
 * đơn điện tử đã xuất (đối chiếu SAI TỪNG ĐỒNG là vi phạm pháp lý, không chỉ là
 * bug UI).
 *
 * Field ở đây khớp `OrderSummary`/`OrderServiceLine`/`OrderTaxBreakdown`
 * (`tcr-erp-shared/src/server/schemas/order/order.schema.ts`) — snake_case,
 * đúng ADR-013. Tiền có thể tới dưới dạng `Types.Decimal128` (BE) đã serialize
 * thành chuỗi hoặc `{ $numberDecimal }` (JSON) — khai `unknown` và ép qua `num()`
 * (alias `toNumber` từ `@/utils/format`), không tin type khai báo.
 */

/** Một nhóm thuế suất đã lưu — khớp `OrderTaxBreakdown`. */
export interface OrderDetailTaxBreakdown {
  vat_rate: unknown;
  taxable_amount: unknown;
  vat_amount: unknown;
}

/** Một dòng dịch vụ cấp đơn đã lưu — khớp `OrderServiceLine`. */
export interface OrderDetailServiceLine {
  kind: PosServiceLineKind;
  service_id?: string | null;
  service_code?: string | null;
  service_name: string;
  unit_name: string;
  amount: unknown;
  vat_rate: unknown;
  vat_rate_id?: string | null;
  vat_source: PosVatSource;
  vat_amount: unknown;
  taxable: boolean;
}

/** Khối vận chuyển đã lưu — chỉ khai những field khối tiền cần đọc. */
export interface OrderDetailShipping {
  cod_amount?: unknown;
  debt_amount?: unknown;
  /** `'self' | 'carrier'`. Chỉ đơn tự VC mới có dòng dịch vụ phí ship. */
  shipping_mode?: ShippingMode | null;
  /**
   * Ai chịu phí vận chuyển. Phí ship CHỈ bị trừ khỏi doanh thu khi shop tự chở
   * VÀ shop trả phí — đó là chi phí thật shop bỏ ra cho đơn (TCR-1084/1086).
   */
  fee_payer?: ShippingFeePayer | null;
  /** Phí vận chuyển đã lưu trên đơn — nguồn duy nhất cho fallback dòng ship. */
  shipping_cost?: unknown;
}

/** `summary` đã lưu — khớp `OrderSummary`. */
export interface OrderDetailSummary {
  original_price?: unknown;
  direct_discount?: unknown;
  voucher_discount?: unknown;
  final_price?: unknown;
  vat?: unknown;
  total?: unknown;
  /** Snapshot lúc tạo đơn. Thiếu (đơn cũ) ⇒ `orderToMoneyResult` trả `[]`, KHÔNG tự tính lại. */
  tax_breakdowns?: OrderDetailTaxBreakdown[];
  /**
   * "Doanh thu đơn hàng" — CHƯA gồm VAT, trừ thêm phí ship CHỈ khi tự vận
   * chuyển VÀ người bán trả (TCR-1084/1086). Khác `net_revenue` ở các đơn tự
   * vận chuyển. Thiếu (đơn cũ) ⇒ suy lại theo chính quy tắc đó từ snapshot đã
   * lưu — xem `fallbackOrderRevenue`.
   */
  order_revenue?: unknown;
  /**
   * Doanh thu chưa VAT cho kế toán — tổng cơ sở tính thuế, không cộng/trừ phí
   * ship. Thiếu (đơn cũ) ⇒ rơi về `final_price`.
   */
  net_revenue?: unknown;
  /**
   * Phần phí vận chuyển do CTKM freeship gánh (đã kẹp). Phí GỐC vẫn nằm ở
   * `shipping.shipping_cost`. Thiếu (đơn cũ) ⇒ 0.
   */
  shipping_discount?: unknown;
  /**
   * TCR-1352 — số tiền giảm do đổi điểm tích luỹ. CHỈ để hiển thị: `final_price`
   * (nguồn của `goodsTaxableAmount`) BE ghi lúc tạo đơn đã trừ sẵn khoản này,
   * nên KHÔNG được trừ thêm lần nữa ở đây.
   */
  loyalty_discount?: unknown;
}

/**
 * Phần của đơn đã lưu mà `orderToMoneyResult` cần đọc. Màn chi tiết/sửa đơn
 * truyền thẳng object `order` từ `useOrderDetail` — object đó có nhiều field
 * khác không liên quan tới khối tiền, structural typing cho phép dư thừa.
 */
export interface OrderDetail {
  summary: OrderDetailSummary;
  /** Đơn cũ (trước khi có bảng dịch vụ cấp đơn) không có field này. */
  service_lines?: OrderDetailServiceLine[];
  /** TCR-977/978. Vẫn được BE ghi song song với `service_lines` cho báo cáo cũ. */
  surcharge_amount?: unknown;
  shipping?: OrderDetailShipping;
}

/** Ép giá trị tiền `unknown` (Decimal128 dạng chuỗi/object, hoặc number) về number. */
const num = (value: unknown): number => toNumber(value);

/** `undefined`/`null` ⇒ dùng `fallback` đã có sẵn trong đơn, KHÔNG bịa số mặc định. */
function numOrFallback(value: unknown, fallback: number): number {
  return value === undefined || value === null ? fallback : num(value);
}

/**
 * Dòng dịch vụ cấp đơn.
 *
 * Ưu tiên `service_lines` đã lưu (đủ thông tin nhất — dịch trực tiếp sang
 * `PosServiceLineResolved`). Đơn CŨ thiếu field này (trước khi bảng dịch vụ ra
 * đời) nhưng có `surcharge_amount > 0` ⇒ dựng ĐÚNG MỘT dòng phụ thu tổng hợp,
 * `vatRate: 0` — không đoán thuế suất thật của đơn cũ.
 *
 * Dòng PHÍ VẬN CHUYỂN của đơn TỰ VC cũ được dựng lại theo cùng khuôn đó
 * (`shipping.shipping_cost`). Thiếu nó thì `OrderInfoPanel` ở nhánh tự VC hiện
 * `shippingLine?.amount ?? 0` = 0 dù đơn có phí ship thật — số tiền biến mất
 * khỏi cả màn chi tiết lẫn màn sửa.
 *
 * Điều kiện fallback ship là "CHƯA có dòng `kind: 'shipping'`", KHÔNG phải
 * "`service_lines` rỗng": đơn có snapshot phụ thu nhưng chưa có snapshot ship
 * (giai đoạn chuyển tiếp) vẫn phải hiện được phí ship.
 *
 * Đơn ĐVVC KHÔNG được dựng dòng này: shop chỉ thu hộ hãng nên phí đó không phải
 * dòng dịch vụ của shop (spec bước 4). Panel hiện nó qua prop `carrierFee` riêng.
 *
 * TCR-1540 — dòng `kind: 'shipping'` khi `shipping.fee_payer === 'buyer'` (đơn
 * TỰ VC, khách trả phí) bị LỌC BỎ khỏi `service_lines` đã lưu TRƯỚC khi map,
 * rồi dựng lại từ `shipping.shipping_cost` như đơn cũ. Lý do: dòng đó không
 * phải "dòng tiền hiển thị" — nó là SNAPSHOT CHO CHỨNG TỪ KẾ TOÁN mà BE
 * (`pos-fulfillment-orchestrator.service.ts` →
 * `snapshotBuyerPaidShippingServiceLine`) ghi lúc "Đi đơn", đã BÓC VAT theo
 * ADR-029: phí ship gộp VAT 30.000 = doanh thu 27.778 + VAT 2.222. Khối tiền ở
 * đây phải hiện số khách THỰC TRẢ (30.000, gồm VAT), không phải phần doanh thu
 * đã tách thuế của kế toán — hiện thẳng `amount` (27.778) làm màn chi tiết/sửa
 * đơn hụt đúng phần VAT.
 *
 * KHÔNG cộng `amount + vat_amount` để ra lại 30.000: đơn cũ (trước ADR-029,
 * KHÔNG có `fee_payer` snapshot kiểu này) có dòng ship với `amount` là số GỐC
 * và `vat_amount` cộng THÊM bên trên — cộng lại sẽ lệch (vd 30.000 + 2.400 =
 * 32.400), y hệt bug số nhân đôi mà comment "KHÔNG nhân đôi" ở test đã chặn.
 * Vì vậy phải BỎ dòng snapshot rồi dựng lại từ `shipping.shipping_cost` (nguồn
 * duy nhất, luôn là số khách thực trả) thay vì cộng dồn hai field trên dòng đã lưu.
 *
 * Đây là CÙNG quy tắc BE đã áp ở tầng đơn (danh sách/tổng hợp) —
 * `orders-aggregate.service.ts` filter `!(line.kind === 'shipping' &&
 * source.shipping?.fee_payer === 'buyer')` trước khi build `savedServiceLines`.
 * FE áp lại đúng biểu thức đó ở đây để hai màn không lệch số.
 */
function resolveServiceLines(order: OrderDetail): PosServiceLineResolved[] {
  const rawLines = order.service_lines ?? [];
  // TCR-1540: bỏ dòng snapshot kế toán (buyer trả phí, đã bóc VAT) TRƯỚC khi
  // map — xem giải thích đầy đủ ở JSDoc trên hàm.
  const savedLines = rawLines.filter(
    (line) => !(line.kind === 'shipping' && order.shipping?.fee_payer === 'buyer'),
  );

  const lines: PosServiceLineResolved[] =
    savedLines.length > 0
      ? savedLines.map((line) => ({
          kind: line.kind,
          serviceId: line.service_id ?? null,
          serviceCode: line.service_code ?? null,
          serviceName: line.service_name,
          unitName: line.unit_name,
          amount: num(line.amount),
          vatRate: num(line.vat_rate),
          vatRateId: line.vat_rate_id ?? null,
          vatSource: line.vat_source,
          taxable: line.taxable,
          vatAmount: num(line.vat_amount),
          // TCR-2224 — `PosServiceLineResolved.lineTotal` bắt buộc từ shared; dòng không chịu thuế không vào số khách trả.
          lineTotal: line.taxable ? Math.round(num(line.amount) + num(line.vat_amount)) : 0,
        }))
      : [];

  // Xét mảng GỐC (`rawLines`), KHÔNG phải mảng đã lọc `savedLines`: đơn chỉ có
  // đúng một dòng snapshot ship (buyer trả phí) vẫn có `rawLines.length > 0`,
  // nên KHÔNG được coi là "đơn cũ chưa có bảng service_lines" — nếu xét theo
  // mảng đã lọc (rỗng), sẽ dựng NHẦM một dòng phụ thu từ `order.surcharge_amount`
  // dù đơn hiện đại đã có bảng dịch vụ (chỉ là dòng ship bị lọc ở trên).
  if (rawLines.length === 0) {
    const surcharge = num(order.surcharge_amount);
    if (surcharge > 0) {
      lines.push({
        kind: 'surcharge',
        serviceId: null,
        serviceCode: null,
        serviceName: 'Phụ thu',
        unitName: 'Lần',
        amount: surcharge,
        vatRate: 0,
        vatRateId: null,
        vatSource: 'default_setting',
        taxable: true,
        vatAmount: 0,
        lineTotal: Math.round(surcharge),
      });
    }
  }

  const shippingCost = num(order.shipping?.shipping_cost);
  if (
    order.shipping?.shipping_mode === 'self' &&
    shippingCost > 0 &&
    !lines.some((line) => line.kind === 'shipping')
  ) {
    lines.push({
      kind: 'shipping',
      serviceId: null,
      serviceCode: null,
      serviceName: 'Phí vận chuyển',
      unitName: 'Lần',
      amount: shippingCost,
      // KHÔNG đoán thuế suất: đơn cũ không lưu thuế suất của phí ship, và bịa
      // một con số ra đây sẽ làm khối tiền lệch khỏi hoá đơn đã xuất.
      vatRate: 0,
      vatRateId: null,
      vatSource: 'default_setting',
      taxable: true,
      vatAmount: 0,
      lineTotal: Math.round(shippingCost),
    });
  }

  return lines;
}

/**
 * Bảng nhóm thuế suất. Thiếu snapshot (đơn cũ) ⇒ `[]` — panel tự ẩn nút "chi
 * tiết" khi mảng rỗng (xem `OrderInfoPanel.money.test.tsx`). TUYỆT ĐỐI KHÔNG tự
 * gom lại từ dòng hàng/dịch vụ: đó chính là hành vi "tính lại pipeline" bị cấm.
 */
function resolveTaxBreakdowns(order: OrderDetail): PosTaxGroup[] {
  const saved = order.summary.tax_breakdowns;
  if (!saved || saved.length === 0) return [];
  return saved.map((group) => ({
    vatRate: num(group.vat_rate),
    taxableAmount: num(group.taxable_amount),
    vatAmount: num(group.vat_amount),
  }));
}

/**
 * Doanh thu của đơn CŨ — đơn không có snapshot `summary.order_revenue`.
 *
 * Suy lại theo ĐÚNG quy tắc mới (TCR-1084/1086, `computePosMoney` bước 8) từ
 * chính số đã lưu trên đơn:
 *
 *   final_price + Σ phụ thu đã lưu − phí ship (CHỈ khi tự VC + người bán trả)
 *
 * Vẫn là ĐỌC snapshot, không tính lại theo giá/thuế hôm nay. Trước đây hàm này
 * rơi về `summary.total` (ĐÃ gồm VAT) còn cột doanh thu ở danh sách đơn rơi về
 * một công thức thứ hai (trừ VAT, trừ cả phí ĐVVC) — cùng một đơn ra hai số
 * khác nhau ở hai màn, đúng thứ spec §3 cấm.
 *
 * `serviceLines` truyền vào là kết quả `resolveServiceLines` (đã xử lý đơn cũ
 * chỉ có `surcharge_amount`). Dòng `kind: 'shipping'` KHÔNG được cộng vào đây:
 * phí ship chỉ xuất hiện ở vế trừ.
 */
function fallbackOrderRevenue(
  order: OrderDetail,
  goodsTaxableAmount: number,
  serviceLines: PosServiceLineResolved[],
): number {
  const surchargeRevenue = serviceLines
    .filter((line) => line.kind === 'surcharge')
    .reduce((sum, line) => sum + Math.max(0, line.amount), 0);
  const selfShipSellerFee =
    order.shipping?.shipping_mode === 'self' && order.shipping?.fee_payer === 'seller'
      ? Math.max(0, num(order.shipping?.shipping_cost))
      : 0;
  return goodsTaxableAmount + surchargeRevenue - selfShipSellerFee;
}

export function orderToMoneyResult(order: OrderDetail): PosMoneyResult {
  const { summary } = order;
  const serviceLines = resolveServiceLines(order);

  const totalGoods = num(summary.original_price);
  const totalLineDiscount = num(summary.direct_discount);
  const orderDiscount = num(summary.voucher_discount);
  // `final_price` là CHÍNH `goodsTaxableAmount` mà BE ghi lúc tạo đơn
  // (`pos-order.service.ts`, từ `computePosMoney` — `allocationBase -
  // orderDiscount`, đã gồm đủ voucher + trade + loyalty). Đọc thẳng, không tự
  // trừ `totalGoods - totalLineDiscount` (bỏ sót voucher — lỗi Critical Task 18).
  const goodsTaxableAmount = num(summary.final_price);
  const totalVat = num(summary.vat);
  const customerPayable = num(summary.total);

  // `cod`/`debt` đọc thẳng số đã lưu (BE giữ bất biến `cod + debt = codDefault`
  // qua `recomputeDebtAfterCodChange`) — không kẹp lại, không suy luận thêm.
  const cod = num(order.shipping?.cod_amount);
  const debt = num(order.shipping?.debt_amount);
  const codDefault = cod + debt;

  return {
    totalGoods,
    totalLineDiscount,
    orderDiscount,
    // Đơn đã lưu không có snapshot phần thực áp từng khoản; màn chi tiết/sửa
    // không đọc field này (chỉ popup POS dùng) nên để 0.
    appliedOrderDiscounts: { voucher: 0, loyalty: 0, trade: 0 },
    // Màn chi tiết/sửa đơn không cần VAT từng dòng hàng — bảng sản phẩm đã
    // hiển thị riêng, và đơn đã lưu không có snapshot phân bổ theo dòng.
    goodsLines: [],
    serviceLines,
    goodsTaxableAmount,
    // Đơn đã lưu không có field riêng cho tổng chịu thuế gồm cả dịch vụ — dùng
    // `goodsTaxableAmount` làm xấp xỉ hiển thị, đúng quyết định Task 18
    // (`readOnlyMoney.totalTaxableAmount`, `edit.tsx`). Field này không được
    // `OrderInfoPanel` render trực tiếp (panel dùng `goodsTaxableAmount`).
    totalTaxableAmount: goodsTaxableAmount,
    taxBreakdowns: resolveTaxBreakdowns(order),
    totalVat,
    customerPayable,
    // Không có field lưu riêng phần phí ĐVVC người mua trả cộng thêm ngoài
    // `customerPayable` (Bước 7 của pipeline) — giữ đúng quyết định Task 18:
    // bằng `customerPayable`, là số đã hiển thị trước khi tách hàm này ra.
    collectTotal: customerPayable,
    // Đơn CŨ (ghi trước khi có freeship) không có field này ⇒ 0, đúng nghĩa
    // "không có khoản giảm phí ship nào", không phải "chưa biết".
    shippingDiscount: num(order.summary.shipping_discount),
    codDefault,
    debt,
    cod,
    // Thiếu (đơn cũ) ⇒ suy lại theo quy tắc mới từ snapshot đã lưu, CÙNG một
    // biểu thức với nhánh fallback của cột doanh thu ở danh sách đơn
    // (`orders-aggregate.service.ts`) — KHÔNG bịa số mặc định.
    orderRevenue: numOrFallback(
      summary.order_revenue,
      fallbackOrderRevenue(order, goodsTaxableAmount, serviceLines),
    ),
    netRevenue: numOrFallback(summary.net_revenue, goodsTaxableAmount),
  };
}
